import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowBack, ChargerEV } from '../../assets/icons';
import { useCharger } from '../../hooks/useChargers';
import { useCompany } from '../../hooks/useCompany';
import { useNavigateWithSearchParam } from '../../hooks/useNavigateWithSearchParam';
import { LicenseSelfServeRenewable } from '../../stores/types/licenses.interface';
import { convertToLocaleCurrency } from '../../utils/Currency.Util';
import {
  Button,
  ButtonSize,
  ButtonType,
  ColorType,
  Grid,
  Label,
  LabelType,
  MODAL_TYPES,
  Pill,
  useGlobalModalContext,
} from '../_ui';
import { Tooltip, TooltipContent, TooltipTrigger } from '../_ui/Tooltip.component';
import SelfServiceRenewalCheckout from './SelfServeRenewalCheckout.component';
import { getLicenseType } from './utils';

interface LicenseRenewalProduct {
  licenseId: string;
  licenseKey: string;
  licenseType: string;
  includes4G: boolean;
  assignedCharger?: string;
  ports?: number;
  currency: string;
  priceAmountCents: number;
  priceAmountCentsWithoutDiscount: number;
  termMonths: number;
  discountAmountCents?: number;
}

interface LicenseRenewalOptionDetails {
  products: Array<LicenseRenewalProduct>;
  currency: string;
  totalAmountCents: number;
  totalAmountCentsWithoutDiscount: number;
  discountAmountCents?: number;
  discountPercent?: number;
}

const convertToOptionDetailsMap = (
  renewables: Array<LicenseSelfServeRenewable>,
  baseTermMonths: number,
) => {
  const optionDetailsMap = new Map<number, LicenseRenewalOptionDetails>();
  const basePriceMap = new Map<string /* license id */, number /* price */>();
  renewables.forEach((renewable) => {
    renewable.renewalOptions.forEach((option) => {
      if (!optionDetailsMap.has(option.termMonths)) {
        optionDetailsMap.set(option.termMonths, {
          products: [],
          currency: option.currency,
          totalAmountCents: 0,
          totalAmountCentsWithoutDiscount: 0,
        } as LicenseRenewalOptionDetails);
      }
      const details = optionDetailsMap.get(option.termMonths) as LicenseRenewalOptionDetails;
      details.products.push({
        licenseId: renewable.licenseId,
        licenseKey: renewable.licenseKey,
        licenseType: renewable.type,
        includes4G: renewable.includes4G,
        assignedCharger: renewable.assignedCharger,
        ports: renewable.ports,
        currency: option.currency,
        priceAmountCents: option.amountCents,
        priceAmountCentsWithoutDiscount: option.amountCents,
        termMonths: option.termMonths,
      });
      details.totalAmountCents += option.amountCents;
      details.totalAmountCentsWithoutDiscount = details.totalAmountCents;
      if (option.termMonths === baseTermMonths) {
        basePriceMap.set(renewable.licenseId, option.amountCents);
      }
    });
  });
  const baseTotalAmountCents = Array.from(basePriceMap.values()).reduce(
    (acc, price) => acc + price,
    0,
  );
  optionDetailsMap.forEach((details, termMonths) => {
    if (termMonths > baseTermMonths) {
      const totalWithoutDiscount = (baseTotalAmountCents * termMonths) / baseTermMonths;
      const totalDiscount = totalWithoutDiscount - details.totalAmountCents;
      if (totalDiscount > 0) {
        details.totalAmountCentsWithoutDiscount = totalWithoutDiscount;
        details.discountAmountCents = totalDiscount;
        details.discountPercent = Math.round((totalDiscount / totalWithoutDiscount) * 100);
      }
      details.products.forEach((product) => {
        const basePrice = basePriceMap.get(product.licenseId);
        if (basePrice) {
          const priceWithoutDiscount = (basePrice * termMonths) / baseTermMonths;
          const discount = priceWithoutDiscount - product.priceAmountCents;
          if (discount > 0) {
            product.priceAmountCentsWithoutDiscount = priceWithoutDiscount;
            product.discountAmountCents = discount;
          }
        }
      });
    }
  });

  return optionDetailsMap;
};

const showTextWithTooltip = (text: string) => {
  return (
    <div className='flex items-center'>
      <Tooltip>
        <TooltipTrigger>
          <Label
            className='max-w-[120px] whitespace-nowrap truncate'
            text={text}
            type={LabelType.BODY3}
            color={ColorType.BLACK}
          />
        </TooltipTrigger>
        <TooltipContent style={{ maxWidth: 'fit-content' }}>
          <Label text={text || ''} type={LabelType.BODY3} color={ColorType.WHITE} />
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

const SelfServiceRenewalSelectModal = memo(
  ({
    renewables,
    initialSelectedTerm,
  }: {
    renewables: Array<LicenseSelfServeRenewable>;
    initialSelectedTerm?: number;
  }) => {
    const { t } = useTranslation();
    const { getChargerNameById } = useCharger();
    const { companyId } = useCompany();
    const { navigateWithCompanyId } = useNavigateWithSearchParam();
    const optionDetailsMap = convertToOptionDetailsMap(renewables, 12);

    const getColumnTitle = () => {
      const columnData = [
        {
          key: 'licenseKey',
          title: t('license_key'),
          component: (row: LicenseRenewalProduct) => {
            return showTextWithTooltip(row.licenseKey);
          },
        },
        {
          key: 'assignedCharger',
          title: t('charger'),
          class: 'w-[25%]',
          component: (row: LicenseRenewalProduct) => {
            return (
              row.assignedCharger &&
              getChargerNameById(row.assignedCharger) && (
                <Tooltip>
                  <TooltipTrigger>
                    <div className='flex items-center max-w-[120px] truncate py-1.5'>
                      <Pill
                        iconLeft={ChargerEV}
                        label={getChargerNameById(row.assignedCharger)}
                        onClick={() =>
                          navigateWithCompanyId(`/chargers/${row.assignedCharger}`, companyId)
                        }
                        labelClass='cursor-pointer'
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <Label
                      text={getChargerNameById(row.assignedCharger)}
                      type={LabelType.BODY3}
                      color={ColorType.WHITE}
                    />
                  </TooltipContent>
                </Tooltip>
              )
            );
          },
        },
        {
          key: 'type',
          title: t('license_type'),
          class: 'w-[60px]',
          component: (row: LicenseRenewalProduct) => (
            <Label
              className='w-[60px] items-center'
              text={t(getLicenseType(row.licenseType))}
              type={LabelType.BODY3}
              color={ColorType.BLACK}
            />
          ),
        },
        {
          key: 'ports',
          title: t('ports'),
          component: (row: LicenseRenewalProduct) => (
            <Label text={row.ports} type={LabelType.BODY3} color={ColorType.BLACK} />
          ),
        },
        {
          key: 'includes4G',
          title: t('4g_text'),
          component: (row: LicenseRenewalProduct) => (
            <Label
              text={t(row.includes4G ? 'yes' : 'no')}
              type={LabelType.BODY3}
              color={ColorType.BLACK}
            />
          ),
        },
        {
          key: 'price',
          title: t('price'),
          class: 'text-right pr-3',
          component: (row: LicenseRenewalProduct) => (
            <div className='text-right pr-3'>
              <Label
                text={convertToLocaleCurrency(
                  row.priceAmountCentsWithoutDiscount / 100,
                  row.currency,
                )}
                type={LabelType.BODY3}
                color={ColorType.BLACK}
              />
              {row.discountAmountCents && (
                <Label
                  text={`-${convertToLocaleCurrency(row.discountAmountCents / 100, row.currency)}`}
                  type={LabelType.BODY4}
                  color={ColorType.POSITIVE}
                />
              )}
            </div>
          ),
        },
      ];
      return columnData;
    };

    const SelectableRenewalGrid = () => {
      const { showModal, hideModal } = useGlobalModalContext();
      const termYearOptions = [1, 3, 5];
      const [selectedOption, setSelectedOption] = useState(initialSelectedTerm || 1);
      const optionDetails = optionDetailsMap.get(selectedOption * 12);
      const selectedProducts =
        optionDetails?.products.map((product) => ({
          licenseId: product.licenseId,
          duration: product.termMonths,
        })) || [];

      const goBack = () => {
        hideModal();
        showModal(MODAL_TYPES.INFO_MODAL, {
          title: t('license_renewal'),
          width: '561px',
          height: 'max-content',
          onRenderBody: () => (
            <SelfServiceRenewalSelectModal
              renewables={renewables}
              initialSelectedTerm={selectedOption}
            />
          ),
          shouldCloseOnOverlayClick: false,
        });
      };

      const handleContinueClick = () => {
        showModal(MODAL_TYPES.INFO_MODAL, {
          title: t('proceed_to_stripe_title'),
          width: '520px',
          height: 'max-content',
          icon: ArrowBack,
          onRenderBody: () => <SelfServiceRenewalCheckout renewals={selectedProducts} />,
          onIconClick: () => goBack(),
          shouldCloseOnOverlayClick: false,
        });
      };

      return (
        <div>
          <Label
            text={t('renewal_term')}
            type={LabelType.H5}
            color={ColorType.BLACK}
            className='mt-4'
          />
          <div className='flex gap-2 mt-2'>
            {termYearOptions.map((option) => (
              <div className='w-full' key={`term_year_${option}`}>
                {optionDetailsMap.get(option * 12)?.discountPercent && (
                  <div className='relative'>
                    <div className='border-[1px] border-positive absolute -top-2 -right-1 rounded-full bg-white'>
                      <Label
                        type={LabelType.LABEL_XXS}
                        color={ColorType.POSITIVE}
                        text={t('save_percentage', {
                          percentage: optionDetailsMap.get(option * 12)?.discountPercent,
                        })}
                        className='pl-1 pr-1 pt-0.5 pb-0.5'
                      />
                    </div>
                  </div>
                )}
                <button
                  key={option}
                  type='button'
                  className={`flex rounded items-center gap-2 rounded hover:bg-denim_light text-base font-semibold
                  font-inter h-12 py-3 px-4 w-full  flex-row justify-center
                  ${
                    selectedOption === option ? 'border-2 border-brand2 bg-denim_light' : 'bg-grey1'
                  }`}
                  onClick={() => setSelectedOption(option)}
                >
                  <div className='flex flex-row justify-between items-center'>
                    <Label
                      type={LabelType.BODY3}
                      color={selectedOption === option ? ColorType.DENIM : ColorType.BLACK}
                      text={option > 1 ? `${option} ${t('years')}` : `${option} ${t('year')}`}
                    />
                  </div>
                </button>
              </div>
            ))}
          </div>
          <div className='divide-y divide-grey2'>
            <Label
              text={t('licenses_to_be_renewed')}
              type={LabelType.H5}
              color={ColorType.BLACK}
              className='mt-6 mb-2'
            />
            <Grid
              primaryKey='licenseKey'
              columns={getColumnTitle()}
              data={optionDetailsMap.get(selectedOption * 12)?.products || []}
              translateClass='translate-x-2.5'
            />
          </div>
          <div className='flex flex-row mt-4 mr-3' style={{ justifyContent: 'space-between' }}>
            <Label text={t('subtotal')} type={LabelType.BODY3} color={ColorType.BLACK} />
            <Label
              text={convertToLocaleCurrency(
                (optionDetails?.totalAmountCentsWithoutDiscount || 0) / 100,
                optionDetails?.currency,
              )}
              type={LabelType.BODY3}
              color={ColorType.BLACK}
            />
          </div>
          {optionDetailsMap.get(selectedOption * 12)?.discountAmountCents && (
            <div className='flex flex-row mt-2 mr-3' style={{ justifyContent: 'space-between' }}>
              <Label
                text={`${t('discount')} (${optionDetails?.discountPercent}%)`}
                type={LabelType.BODY3}
                color={ColorType.POSITIVE}
              />
              <Label
                text={`-${convertToLocaleCurrency(
                  (optionDetails?.discountAmountCents || 0) / 100,
                  optionDetails?.currency,
                )}`}
                type={LabelType.BODY3}
                color={ColorType.POSITIVE}
              />
            </div>
          )}
          <div className='flex flex-row mt-2 mb-6 mr-3' style={{ justifyContent: 'space-between' }}>
            <Label text={t('total_before_tax')} type={LabelType.LABEL_M} color={ColorType.BLACK} />
            <Label
              text={convertToLocaleCurrency(
                (optionDetails?.totalAmountCents || 0) / 100,
                optionDetails?.currency,
              )}
              type={LabelType.LABEL_M}
              color={ColorType.BLACK}
            />
          </div>
          <div className='sticky'>
            <Button
              onClick={handleContinueClick}
              label={t('continue')}
              type={ButtonType.PRIMARY}
              size={ButtonSize.SMALL_FULL}
            />
          </div>
        </div>
      );
    };

    return (
      <div className='mb-5'>
        <div className='divide-y divide-grey2'>
          <div>
            <Label
              text={t('start_renewal_subtitle_1')}
              type={LabelType.BODY3}
              color={ColorType.BLACK}
            />
            <Label
              text={t('start_renewal_subtitle_2')}
              type={LabelType.BODY3}
              color={ColorType.BLACK}
              className='mt-2 mb-4'
            />
          </div>
          <SelectableRenewalGrid />
        </div>
      </div>
    );
  },
);

export default SelfServiceRenewalSelectModal;
